import { computed, Signal } from '@angular/core';
import {
  patchState,
  signalStoreFeature,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { interval, Subscription } from 'rxjs';

export interface CountdownState {
  timeElapsed: number;
  isRunning: boolean;
  startedAt: number | null;
}

const initialCountdownState: CountdownState = {
  timeElapsed: 0,
  isRunning: false,
  startedAt: null,
};

/**
 * Custom SignalStore feature for countdown functionality
 *
 * Provides persistent countdown state with RxJS-based timer management.
 * The countdown persists across component destruction/recreation.
 *
 * @param expiresAt Signal containing the expiration date/time
 * @returns SignalStore feature with countdown capabilities
 */
export function withCountdown(expiresAt: Signal<Date | null>) {
  let countdownSubscription: Subscription | null = null;

  return signalStoreFeature(
    withState<CountdownState>(initialCountdownState),
    withComputed(({ timeElapsed, startedAt }) => {
      const totalDuration = computed(() => {
        const expires = expiresAt();
        const started = startedAt();
        if (!expires || !started) return 0;

        return Math.max(0, Math.floor((expires.getTime() - started) / 1000));
      });

      return {
        timeRemaining: computed(() => {
          const expires = expiresAt();
          if (!expires) return 0;

          const now = Date.now();
          const remaining = expires.getTime() - now;
          return Math.max(0, Math.floor(remaining / 1000));
        }),
        isExpired: computed(() => {
          const expires = expiresAt();
          if (!expires) return false;

          return Date.now() >= expires.getTime();
        }),
        totalDuration,
        progress: computed(() => {
          const total = totalDuration();
          const elapsed = timeElapsed();
          if (total === 0) return 0;

          return Math.min(100, Math.max(0, (elapsed / total) * 100));
        }),
      };
    }),
    withMethods((store) => {
      function stopCountdown(): void {
        if (countdownSubscription) {
          countdownSubscription.unsubscribe();
          countdownSubscription = null;
        }
        patchState(store, { isRunning: false });
      }

      function startCountdown(): void {
        if (countdownSubscription || store.isRunning()) return;

        const now = Date.now();
        patchState(store, {
          isRunning: true,
          startedAt: store.startedAt() || now,
        });

        countdownSubscription = interval(1000).subscribe(() => {
          if (store.isExpired()) {
            stopCountdown();
            return;
          }

          patchState(store, (state) => ({
            timeElapsed: state.timeElapsed + 1,
          }));
        });
      }

      return {
        startCountdown,
        stopCountdown,
        resetCountdown(): void {
          stopCountdown();
          patchState(store, {
            timeElapsed: 0,
            startedAt: null,
          });
        },
        pauseCountdown(): void {
          stopCountdown();
        },
        resumeCountdown(): void {
          if (!store.startedAt()) {
            startCountdown();
            return;
          }
          const now = Date.now();
          const started = store.startedAt();
          if (started) {
            const actualElapsed = Math.floor((now - started) / 1000);
            patchState(store, { timeElapsed: actualElapsed });
          }
          startCountdown();
        },
        setTimeElapsed(seconds: number): void {
          patchState(store, { timeElapsed: Math.max(0, seconds) });
        },
      };
    }),
    withHooks((store) => ({
      onInit: () => {
        store.startCountdown();
      },
    })),
  );
}
